import { useForm } from "react-hook-form";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import GoogleLogo from "@/assets/icons/google-logo.svg";
import MicrosoftLogo from "@/assets/icons/microsoft-logo.svg";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import ErrorText from "@/components/universal/error-text";
import {
  handleCredentialSignIn,
  handleGoogleSignIn,
  handleMicrosoftSignIn,
  usePendingCredential,
  useSsoSignIn,
} from "./-auth-functions";
import type { JSX } from "react";

export const Route = createFileRoute("/auth/login")({
  component: LoginPage,
});

interface LoginForm {
  email: string;
  password: string;
}

/**
 * Login page for users to sign in
 * @returns {JSX.Element} LoginPage component
 */
function LoginPage(): JSX.Element {
  const form = useForm<LoginForm>({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const {
    set: setPendingCredential,
    get: getPendingCredential,
    clear: clearPendingCredential,
  } = usePendingCredential();

  const { ssoPending, runSsoSignIn } = useSsoSignIn();

  const navigate = useNavigate();
  const { next } = Route.useSearch();
  const onMfaRequired = () =>
    navigate({ to: "/auth/mfa-challenge", search: { next } });

  async function onSubmit(formData: LoginForm) {
    await handleCredentialSignIn({
      form,
      email: formData.email,
      password: formData.password,
      getPendingCredential,
      clearPendingCredential,
      onMfaRequired,
    });
  }

  return (
    <div className="flex w-full max-w-sm flex-col gap-6">
      <title>Login - Legrande Health Portal</title>
      <Card className="sm:max-w-96 w-full text-center">
        <CardHeader>
          <CardTitle>Welcome Back</CardTitle>
          <CardDescription>Sign in to your account</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center gap-4">
            <Button
              onClick={() =>
                runSsoSignIn(() =>
                  handleGoogleSignIn({
                    form,
                    getPendingCredential,
                    clearPendingCredential,
                    onMfaRequired,
                  }),
                )
              }
              disabled={ssoPending}
              variant={"outline"}
              className="w-full text-md font-normal"
            >
              <img
                className="size-6 mr-2"
                src={GoogleLogo}
                loading="lazy"
                alt="google logo"
              />
              Sign in with Google
            </Button>
            <Button
              onClick={() =>
                runSsoSignIn(() =>
                  handleMicrosoftSignIn({
                    form,
                    setPendingCredential,
                    onMfaRequired,
                  }),
                )
              }
              disabled={ssoPending}
              variant={"outline"}
              className="w-full text-md font-normal"
            >
              <img
                className="size-6 mr-2"
                src={MicrosoftLogo}
                loading="lazy"
                alt="microsoft logo"
              />
              Sign in with Microsoft
            </Button>
          </div>
          <div className="flex items-center justify-center gap-4 mt-4">
            <Separator className="my-4 flex-1" />
            <span className="text-muted-foreground text-sm">or</span>
            <Separator className="my-4 flex-1" />
          </div>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="text-left space-y-4"
            >
              <FormField
                control={form.control}
                rules={{ required: "Email is required" }}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input {...field} type="text" autoComplete="email" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                rules={{ required: "Password is required" }}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="password"
                        autoComplete="current-password"
                      />
                    </FormControl>
                    <FormMessage />
                    <FormDescription>
                      <Link
                        to="/auth/forgot-password"
                        className="text-sm p-0 hover:underline "
                      >
                        Forgot your password?
                      </Link>
                    </FormDescription>
                  </FormItem>
                )}
              />

              <div>
                <Button
                  type="submit"
                  disabled={form.formState.isSubmitting}
                  className="w-full"
                >
                  Sign In
                </Button>
              </div>

              {form.formState.errors.root?.serverError && (
                <ErrorText
                  text={
                    form.formState.errors.root?.serverError.message ||
                    "Error setting password. Please contact support."
                  }
                />
              )}
            </form>
          </Form>
        </CardContent>
      </Card>
      <div className="text-muted-foreground *:[a]:hover:text-primary text-center text-xs text-balance *:[a]:underline *:[a]:underline-offset-4">
        By clicking sign in, you agree to our{" "}
        <Link to="/terms-of-use">Terms of Use</Link> and{" "}
        <Link to="/privacy-policy">Privacy Policy</Link>
      </div>
    </div>
  );
}
